import { Button, FormControl, FormLabel, Grid, Input, Typography } from '@mui/joy';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { OrganizationAutocomplete } from '@/components/form-elements/OrganizationAutocomplete';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ButtonLink } from '@/components/router/ButtonLink';
import { I18N_DE } from '@/i18n/de';
import { CreateOrganizationUserDto, Organization, OrganizationUserRole } from '@/modules/api';
import {
  backendApiOrganizationsCreateOrganizationUserMutation,
  backendApiUsersFindOneOptions,
} from '@/modules/api/@tanstack/react-query.gen';
import { EntityActive } from '@/types/EntityActive';

export const Route = createFileRoute(
  '/_authenticated/_management/administration/benutzerinnen/$userId_/organisation/hinzufuegen',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: CreateOrganizationUserRoute,
});

type FormData = Pick<CreateOrganizationUserDto, 'role'> & {
  active: EntityActive;
};

function CreateOrganizationUserRoute() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization>();

  const { data: user, error } = useQuery(backendApiUsersFindOneOptions({ path: { userId } }));

  const { mutate, isPending } = useMutation({
    ...backendApiOrganizationsCreateOrganizationUserMutation(),
    onSuccess: async () =>
      navigate({
        to: '/administration/organisationen/$organizationId/benutzerinnen/$userId',
        params: { organizationId: organization?.id!, userId },
      }),
  });

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      role: OrganizationUserRole.EDITOR,
      active: EntityActive.FALSE,
    },
  });

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    user && (
      <Grid
        container
        component="form"
        spacing={2}
        onSubmit={handleSubmit(({ active, ...formData }) => {
          if (isPending) return;

          mutate({
            path: { organizationId: organization?.id!, userId },
            body: { active: active === EntityActive.TRUE, ...formData },
          });
        })}
      >
        <Grid md={12}>
          <Typography level="h3">Organisation zuordnen</Typography>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Vorname</FormLabel>
            <Input value={user.firstName} readOnly />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Nachname</FormLabel>
            <Input value={user.lastName} readOnly />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Dienstliche E-Mail-Adresse</FormLabel>
            <Input value={user.email} readOnly />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Telefonnummer</FormLabel>
            <Input value={user.phoneNumber} readOnly />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Institution</FormLabel>
            <Input value={I18N_DE.UserInstitutionType[user.institutionType]} readOnly />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Funktion</FormLabel>
            <Input value={user.roleText} readOnly />
          </FormControl>
        </Grid>

        <Grid md={12}>
          <FormControl>
            <FormLabel>Organisation</FormLabel>
            <OrganizationAutocomplete
              variant="outlined"
              onChange={(event, value) => {
                setOrganization(value || undefined);
              }}
            />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Rolle</FormLabel>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  options={Object.values(OrganizationUserRole)}
                  optionLabel={getI18nRadioLabel({
                    i18nTitle: I18N_DE.OrganizationUserRole,
                    i18nDescription: I18N_DE.OrganizationUserRoleDescription,
                  })}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid md={6}>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Controller
              control={control}
              name="active"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  options={Object.values(EntityActive)}
                  optionLabel={getI18nRadioLabel({
                    i18nTitle: I18N_DE.EntityActive,
                    i18nDescription: I18N_DE.EntityActiveDescription,
                  })}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid md={12}>
          <Stack direction="row" gap={2} justifyContent="space-between" marginTop={4}>
            <ButtonLink variant="outlined" to="/administration/benutzerinnen/$userId" params={{ userId }}>
              Benutzer*in
            </ButtonLink>
            <Button type="submit" disabled={!organization || isPending} loading={isPending}>
              Zuordnen
            </Button>
          </Stack>
        </Grid>
      </Grid>
    )
  );
}
